import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";

import { db, auth } from "../firebase";
import { fetchFirestoreEvents } from "./firebaseService";

// Normalizes the mixed date formats stored across collections
// (Firestore Timestamp on cases, ISO strings on invoices/files).
const toJsDate = (value) => {
  if (!value) return null;

  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const timeAgo = (date) => {
  const parsed = toJsDate(date);
  if (!parsed) return "";

  const minutes = Math.floor((Date.now() - parsed.getTime()) / 60000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getDashboardStats = async (uid) => {
  try {
    const pendingRef = collection(db, "pending");
    const disposedRef = collection(db, "disposed");

    const pendingQuery = query(
      pendingRef,
      where("owner", "==", uid)
    );

    const disposedQuery = query(
      disposedRef,
      where("owner", "==", uid)
    );

    const [pendingSnapshot, disposedSnapshot] =
      await Promise.all([
        getDocs(pendingQuery),
        getDocs(disposedQuery),
      ]);

   let pending = 0;
let today = 0;
let myCases = 0;
let disposed = disposedSnapshot.size;

let inProgress = 0;
let onHold = 0;

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    pendingSnapshot.forEach((doc) => {
      const data = doc.data();

      myCases++;

      const status = data.status?.toLowerCase();


switch (status) {
  case "in progress":
    inProgress++;
    pending++;
    break;

  case "on hold":
    onHold++;
    pending++;
    break;

  case "disposed":
  case "dismissed":
  case "rejected":
    break;

  default:
    pending++;
}

      if (data.nextHearingDate) {
        const [day, month, year] =
          data.nextHearingDate.split("/").map(Number);

        const hearingDate = new Date(
          year,
          month - 1,
          day
        );

        hearingDate.setHours(0, 0, 0, 0);

        if (
          hearingDate.getTime() ===
          todayDate.getTime()
        ) {
          today++;
        }
      }
    });

    return {
  pendingCases: pending,
  disposedCases: disposed,
  todayCases: today,
  myCases,

  inProgressCases: inProgress,
  onHoldCases: onHold,

  totalCases: pending + disposed,
};
  } catch (err) {
    console.error(err);

    return {
      pendingCases: 0,
      disposedCases: 0,
      todayCases: 0,
      myCases: 0,
    };
  }
};

export const getUpcomingHearings = async () => {
  try {
    const { events } = await fetchFirestoreEvents();

    return events
      .sort((a, b) => {
        const first = new Date(`${a.date} ${a.time}`);
        const second = new Date(`${b.date} ${b.time}`);

        return first - second;
      })
      .slice(0, 3);

  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getTodayTasks = async () => {
  try {
    const { events } = await fetchFirestoreEvents();

    // Local date (not toISOString, which is UTC and rolls the date back for
    // IST users before 5:30 AM) — matches both event.date and task.dueDate,
    // which are stored as local yyyy-MM-dd strings.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // Manual calendar events dated today (hearings have a "Case No" and are
    // shown in Upcoming Hearings instead).
    const todayEvents = events.filter(
      (event) => !event["Case No"] && event.date === today
    );

    // Tasks from the new task system (lawyers/{uid}/tasks, the Tasks Board):
    // due today and not completed.
    let todayTaskItems = [];
    const user = auth.currentUser;
    if (user) {
      const snap = await getDocs(collection(db, "lawyers", user.uid, "tasks"));
      todayTaskItems = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.dueDate === today && t.status !== "completed")
        .map((t) => ({
          docId: `task-${t.id}`,
          title: t.title || "Untitled task",
          time: "",
          description:
            t.description ||
            (t.linkedCaseName || t.linkedCaseNo
              ? `Case: ${t.linkedCaseName || t.linkedCaseNo}`
              : ""),
          priority: t.priority
            ? t.priority.charAt(0).toUpperCase() + t.priority.slice(1)
            : "Normal",
        }));
    }

    return [...todayEvents, ...todayTaskItems].sort((a, b) => {
      if (!a.time || !b.time) return 0;
      return a.time.localeCompare(b.time);
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getRecentCases = async (uid) => {
  try {
    const pendingRef = collection(db, "pending");
    const disposedRef = collection(db, "disposed");

    const pendingQuery = query(
      pendingRef,
      where("owner", "==", uid)
    );

    const disposedQuery = query(
      disposedRef,
      where("owner", "==", uid)
    );

    const [pendingSnapshot, disposedSnapshot] =
      await Promise.all([
        getDocs(pendingQuery),
        getDocs(disposedQuery),
      ]);

    const pendingCases = pendingSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const disposedCases = disposedSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const allCases = [...pendingCases, ...disposedCases];

    allCases.sort((a, b) => {
      const first =
        a.timestamp?.seconds || 0;

      const second =
        b.timestamp?.seconds || 0;

      return second - first;
    });

    return allCases.slice(0, 3);

  } catch (error) {
    console.error(error);
    return [];
  }
};

// "My Activity" feed: merges the user's own actions — cases added,
// invoices generated, documents uploaded — newest first.
export const getRecentActivity = async (uid, count = 8) => {
  try {
    const caseSnapsPromise = Promise.all(
      ["pending", "disposed"].map((status) =>
        getDocs(query(collection(db, status), where("owner", "==", uid)))
      )
    );

    const invoicesPromise = getDocs(
      collection(db, "lawyers", uid, "invoices")
    ).catch(() => null);

    const filesPromise = getDocs(
      query(collection(db, "files"), where("lawyer_uid", "==", uid))
    ).catch(() => null);

    const [caseSnaps, invoiceSnap, filesSnap] = await Promise.all([
      caseSnapsPromise,
      invoicesPromise,
      filesPromise,
    ]);

    const items = [];

    caseSnaps.forEach((snap) =>
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const date = toJsDate(data.timestamp);
        if (!date) return;

        const caseName =
          data.petitionerName && data.respondentName
            ? `${data.petitionerName} vs. ${data.respondentName}`
            : data.caseNo || "New case";

        items.push({
          id: `case-${docSnap.id}`,
          type: "create",
          title: "You added a new case",
          subtitle: caseName,
          date,
        });
      })
    );

    invoiceSnap?.forEach((docSnap) => {
      const data = docSnap.data();
      const date = toJsDate(data.createdAt || data.raisedOn);
      if (!date) return;

      items.push({
        id: `invoice-${docSnap.id}`,
        type: "invoice",
        title: "You generated an invoice",
        subtitle: data.invoiceNumber ? `Invoice #${data.invoiceNumber}` : "Invoice",
        date,
      });
    });

    // Legacy uploads without an uploadedAt stamp are skipped — they can't
    // be placed on the timeline.
    filesSnap?.forEach((docSnap) => {
      const data = docSnap.data();
      const date = toJsDate(data.uploadedAt);
      if (!date) return;

      items.push({
        id: `file-${docSnap.id}`,
        type: "upload",
        title: "You uploaded a document",
        subtitle: data.fileName || "Document",
        date,
      });
    });

    items.sort((a, b) => b.date - a.date);

    return items.slice(0, count);
  } catch (error) {
    console.error(error);
    return [];
  }
};

// "Recent Updates" feed: the user's latest notifications (case shares,
// hearing updates, etc.), same source as the topbar bell.
export const getRecentNotifications = async (uid, isClient = false, count = 8) => {
  try {
    const notificationsRef = collection(
      db,
      isClient ? "client" : "lawyers",
      uid,
      "notifications"
    );

    const snap = await getDocs(
      query(notificationsRef, orderBy("timestamp", "desc"), limit(count))
    );

    return snap.docs.map((docSnap) => {
      const data = docSnap.data();

      return {
        id: docSnap.id,
        title: data.title || "Update",
        body: data.body || "",
        date: toJsDate(data.timestamp),
      };
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};